import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Button,
  SvgIcon,
  Container,
  Stack,
} from '@mui/material';
import {
  Home as HomeIcon,
  People as FriendsIcon,
  AccountCircle as ProfileIcon,
  Storefront as ShopIcon,
  Add
} from '@mui/icons-material';
import { useAuth } from '../context/AuthProvider.tsx';
import WizardModal from './WizardModal.tsx';
import UpgradeModal from './UpgradeModal.tsx';
import { fetchCapsules } from '../api/GetCapsules.ts';
import { motion } from 'framer-motion';

const appBarHeight = 70;

const NavigationBar: React.FC = () => {
  const { login, logout, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))
  const location = useLocation();
  const navigate = useNavigate();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  // Add scroll event listener to detect when page is scrolled
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Initialize on mount
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const GradientAddCircleIcon = () => (
    <SvgIcon sx={{ fontSize: isMobile ? 32 : 26 }}>
      <defs>
        <linearGradient id="addIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7B2CBF" />
          <stop offset="100%" stopColor="#E31B88" />
        </linearGradient>
      </defs>
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"
        fill="url(#addIconGradient)"
      />
    </SvgIcon>
  );

  const handleCloseUpgradeModal = () => {
    setUpgradeModalOpen(false);
  };

  const handleOpenWizard = async () => {
    console.log(user?.subscriptionStatus)
    if(user?.subscriptionStatus !== 'premium'){
      const capsules = await fetchCapsules(user!.username);  
      const capsuleCount = capsules.length
      if(capsuleCount > 2){
        setUpgradeModalOpen(true)
      }
      else{
        setIsWizardOpen(true);
      }
    } else{
      setIsWizardOpen(true);
    }
    
  };

  const handleCloseWizard = () => {
    setIsWizardOpen(false);
  };

  const handleSubmitCapsule = (formData: any) => {
    setIsWizardOpen(false);
  };

  const navItems = [
    { text: 'Capsules', path: '/', icon: <HomeIcon /> },
    { text: 'Friends', path: '/friends', icon: <FriendsIcon /> },
    { text: 'Create', icon: <GradientAddCircleIcon />, action: handleOpenWizard },
    { text: 'Shop', path: '/shop', icon: <ShopIcon /> },
    { text: 'Profile', path: `/profile/${user?.username}`, icon: user ? 
      <Avatar 
        sx={{ 
          width: 28, 
          height: 28,
        }} 
        src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${user.username}/profilepic.jpeg` || 'https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/profilepic.svg'}
      >
      </Avatar> 
      : <ProfileIcon /> 
    },
  ];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  
  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleProfileNavigate = (path: string) => {
    navigate(path);
    handleProfileClose();
  };

  const renderDesktopNav = () => (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: 1100, 
        background: !scrolled ? 'none' : '#0A0F15' , // Fully opaque background
        height: appBarHeight,
        borderBottom: !scrolled ? 'none' : '1px solid #3b3b3b',
        transition: 'all 0.3s ease',
      }}
      elevation={0}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: appBarHeight, minHeight: appBarHeight }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: isTablet ? 0 : 2, ml: isTablet ? 2 : 8 }}>
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <img 
                src='/icon.png' 
                height='30px' 
                alt="TimeCapsule Logo"
                style={{marginRight: "15px", marginBottom: '10px'}}
              />
              {!isTablet &&
              <img 
                src='/text-logo2.png' 
                height='40px' 
                alt="TimeCapsule Logo"
                style={{marginBottom: '5px'}}
              />
              }
            </motion.div>
          </Box>
          
          {/* Navigation Links - Styled like lovable.ai (simple text links) */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
            <Button
              onClick={() => navigate('/')}
              sx={{
                color: location.pathname === '/' ? '#fff' : 'rgba(255,255,255,0.85)',
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                mr: 2,
                '&:hover': { backgroundColor: 'transparent', color: '#fff' },
              }}
            >
              Capsules
            </Button>
            <Button
              onClick={() => navigate('/friends')}
              sx={{
                color: location.pathname === '/friends' ? '#fff' : 'rgba(255,255,255,0.85)',
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                mr: 2,
                '&:hover': { backgroundColor: 'transparent', color: '#fff' },
              }}
            >
              Friends
            </Button>
            <Button
              onClick={() => navigate('/shop')}
              sx={{
                color: location.pathname === '/shop' ? '#fff' : 'rgba(255,255,255,0.85)',
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'transparent', color: '#fff' },
              }}
            >
              Shop
            </Button>
            
            {/* Create button is integrated into the horizontal nav */}
            
          </Box>
          
          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Right Side - Social Icons + Auth */}
          <Stack direction="row" spacing={3} alignItems="center">
            {/* Shop button */}
            
            <Button
              onClick={handleOpenWizard}
              endIcon={<Add />}
              sx={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'transparent', color: '#fff' },
              }}
            >
              Create
            </Button>
            
            {/* Auth Section */}
            {user ? (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  onClick={() => handleProfileNavigate(`/profile/${user.username}`)}
                  sx={{ 
                    color: 'white',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'transparent' },
                  }}
                  startIcon={
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                      }}
                      src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${user.username}/profilepic.jpeg` || 'https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/profilepic.svg'}
                    >
                      {user.username.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                >
                  Profile
                </Button>
              </Box>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  onClick={login}
                  sx={{
                    color: '#fff',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    padding: '6px 16px',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Sign in
                </Button>
                <Button
                  onClick={login}
                  variant="contained"
                  sx={{
                    bgcolor: '#fff',
                    color: '#000',
                    borderRadius: 1,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    boxShadow: 'none',
                    padding: '6px 16px',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Sign up
                </Button>
              </Stack>
            )}
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );

  const renderMobileNav = () => (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1300,
        background: 'rgba(10, 15, 21, 0.85)',
        borderTop: '1px solid rgba(255,255,255,0.05)'
      }} 
      elevation={3}
    >
      <BottomNavigation
        value={location.pathname}
        sx={{ 
          height: 58,
          backgroundColor: '#0A0F15',
          boxShadow: 20,
          '& .MuiBottomNavigationAction-root': {
            color: 'rgba(255,255,255,0.5)',
            padding: '6px 0',
            minWidth: 'auto',
            '&.Mui-selected': {
              color: '#fff'
            }
          }
        }}
      >
        {navItems.map((item) => (
          <BottomNavigationAction
            key={item.text}
            icon={item.icon}
            component={item.path ? Link : 'button'}
            to={item.path}
            onClick={item.action}
            value={item.path}
            sx={{ 
              color: location.pathname === item.path ? '#7B2CBF' : 'grey',
              '&.Mui-selected': {
                color: '#7B2CBF',
              },
              '& .MuiAvatar-root': {
                borderColor: location.pathname === item.path ? '#7B2CBF' : 'rgba(123, 44, 191, 0.5)'
              }
            }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );

  return (
    <>
      {isMobile ? renderMobileNav() : renderDesktopNav()}
      <WizardModal
        open={isWizardOpen}
        onClose={handleCloseWizard}
        onSubmit={handleSubmitCapsule}
      />
      <UpgradeModal
          open={upgradeModalOpen}
          onClose={handleCloseUpgradeModal}
        />
    </>
  );
};

export default NavigationBar;
